/**
 * GreetingHeader — "Good morning, {name}!" + a date + tasks-due subtitle.
 *
 * Per Figma (562:22092): subtitle reads "Tuesday, June 7 · {n} tasks need
 * your attention today." The task count is passed in (typically derived
 * from the compliance counts) so this component stays presentation-only.
 *
 * ## Localisation
 * All user-facing strings flow through `react-i18next`'s `t(…)`. Time-
 * of-day greetings ship as full sentences with punctuation in two
 * variants — with and without a name — so locales can handle things
 * like Spanish's inverted `¡…!` or word-order changes around the name
 * (`t("Good morning, {{firstName}}!", { firstName })`). The date is
 * formatted with `toLocaleDateString(i18n.language, …)` so it follows
 * the app's active language rather than the browser/OS locale.
 *
 * Add the matching strings to `client/src/locales/<locale>/translation.json`
 * when introducing new copy.
 */
import { Box, SxProps, Theme, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

export interface GreetingHeaderProps {
  firstName?: string;
  /** Optional formatted date string. Defaults to today. */
  dateLabel?: string;
  /** Number of attention-required tasks. When omitted, the subtitle
   *  shows only the date. */
  tasksDue?: number;
}

const wrap: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--space-2)",
};

const heading: SxProps<Theme> = {
  margin: 0,
};

/**
 * Pick the i18n key for the time-of-day greeting based on the current
 * hour. Each time-of-day has two variants — with and without a name —
 * so the full greeting (including punctuation) lives inside the
 * translation, letting locales handle things like Spanish's inverted
 * `¡…!` or different word order around the name.
 */
function greetingKeyForHour(h: number, hasName: boolean): string {
  if (h < 12) return hasName ? "Good morning, {{firstName}}!" : "Good morning!";
  if (h < 18)
    return hasName ? "Good afternoon, {{firstName}}!" : "Good afternoon!";
  return hasName ? "Good evening, {{firstName}}!" : "Good evening!";
}

/** Locale-aware date string. Uses the app's active i18n language so the
 *  date stays consistent with the surrounding translated copy, rather
 *  than following the browser/OS locale. */
function defaultDateLabel(d: Date, language: string): string {
  return d.toLocaleDateString(language, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

const GreetingHeader: React.FC<GreetingHeaderProps> = ({
  firstName,
  dateLabel,
  tasksDue,
}) => {
  const { t, i18n } = useTranslation();
  const now = new Date();
  const greet = t(greetingKeyForHour(now.getHours(), Boolean(firstName)), {
    firstName,
  });
  const date = dateLabel ?? defaultDateLabel(now, i18n.language);

  // Two natural-English keys cover singular/plural without depending on
  // i18next's plural rules — matches how the rest of the codebase
  // handles count-driven copy.
  const tasksFragment =
    typeof tasksDue === "number"
      ? ` · ${
          tasksDue === 1
            ? t("{{count}} task needs your attention today", {
                count: tasksDue,
              })
            : t("{{count}} tasks need your attention today", {
                count: tasksDue,
              })
        }`
      : "";

  return (
    <Box sx={wrap}>
      <Typography
        variant="h3"
        sx={[
          {
            color: "var(--color-text-primary)",
          },
          ...(Array.isArray(heading) ? heading : [heading]),
        ]}
      >
        {greet}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: "var(--color-text-tertiary)",
        }}
      >
        {date}
        {tasksFragment}
      </Typography>
    </Box>
  );
};

export default GreetingHeader;
